import QRCodeStyling from 'qr-code-styling';

// Helper to convert blob URLs to base64
const convertBlobToBase64 = async (blobUrl) => {
  if (!blobUrl) return '';
  try {
    const response = await fetch(blobUrl);
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error('Error converting blob to base64:', error);
    return '';
  }
};

// Modified SVG to base64 conversion for better quality
const svgToBase64 = async (svgElement) => {
  return new Promise((resolve, reject) => {
    try {
      // Get SVG data with proper sizing
      const svgData = new XMLSerializer().serializeToString(svgElement);
      const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });

      // Create high-resolution canvas (2x size for better quality)
      const canvas = document.createElement('canvas');
      const scale = 2; // Increase resolution
      canvas.width = 140 * scale;
      canvas.height = 140 * scale;
      const ctx = canvas.getContext('2d');

      // Enable better image rendering
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';

      const img = new Image();
      img.onload = () => {
        // Fill white background
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        // Draw scaled image
        ctx.scale(scale, scale);
        ctx.drawImage(img, 0, 0, 140, 140);

        // Get base64 at higher quality
        const base64 = canvas.toDataURL('image/png', 1.0);
        resolve(base64);
      };

      img.onerror = (error) => {
        console.error('Error loading SVG image:', error);
        reject(error);
      };

      img.src = URL.createObjectURL(svgBlob);
    } catch (error) {
      console.error('Error converting SVG to base64:', error);
      reject(error);
    }
  });
};

// Modified getQRCodeBase64 function
const getQRCodeBase64 = async (qrRef) => {
  try {
    // Find SVG element in QR container
    const qrContainer = document.querySelector('#qrContainerRef');
    const svgElement = qrContainer?.querySelector('svg');

    if (!svgElement) {
      console.error('No SVG element found in QR container');
      return '';
    }

    // Convert SVG to base64
    const base64Data = await svgToBase64(svgElement);

    if (!base64Data || !base64Data.startsWith('data:image/png;base64,')) {
      throw new Error('Invalid base64 data generated');
    }

    return base64Data;
  } catch (error) {
    console.error('Error converting QR code to base64:', error);
    return '';
  }
};

// Main copy function
export const copyEmailSignature = async ({ state, qrRef, contentRef, cardUrl }) => {
  console.log("Card URL from copy email signature: ", cardUrl);
  if (!contentRef.current) {
    throw new Error('Content reference is required');
  }

  try {
    console.log('Starting image conversion with state:', {
      includeQR: state.emailSignatureIncludeQRCode,
      includeBanner: state.emailSignatureIncludeBannerImage,
      hasProfilePic: !!state.emailSignatureProfilePictureUrl,
      hasCompanyLogo: !!state.emailSignatureCompanyLogoUrl
    });

    // First combine profile and logo if both exist
    let profileImageUrl = state.emailSignatureProfilePictureUrl;
    if (state.emailSignatureProfilePictureUrl && state.emailSignatureCompanyLogoUrl) {
      profileImageUrl = await combineProfileAndLogo(
        state.emailSignatureProfilePictureUrl,
        state.emailSignatureCompanyLogoUrl
      );
    }

    // Convert all images to base64 in parallel
    const [profileBase64, companyLogoBase64, qrCode, bannerImage] = await Promise.all([
      profileImageUrl ? convertBlobToBase64(profileImageUrl) : Promise.resolve(''),
      !state.emailSignatureProfilePictureUrl && state.emailSignatureCompanyLogoUrl ?
        convertBlobToBase64(state.emailSignatureCompanyLogoUrl) : Promise.resolve(''),
      state.emailSignatureIncludeQRCode ? getQRCodeBase64(qrRef) : Promise.resolve(''),
      state.emailSignatureIncludeBannerImage ? convertBlobToBase64(state.emailSignatureBannerImageUrl) : Promise.resolve('')
    ]);

    const images = {
      profileImage: profileBase64,
      companyLogo: companyLogoBase64,
      qrCode,
      bannerImage
    };

    // Create container for email signature
    const emailContainer = document.createElement('div');
    emailContainer.style.cssText = 'width: 100%; max-width: 600px; margin: 0; padding: 0; background-color: white;';
    emailContainer.innerHTML = generateSignatureHTML(state, images, cardUrl);

    try {
      // Focus the document first
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
      window.focus();

      await navigator.clipboard.write([
        new ClipboardItem({
          'text/html': new Blob([emailContainer.outerHTML], { type: 'text/html' }),
          'text/plain': new Blob([contentRef.current.innerText], { type: 'text/plain' })
        })
      ]);
    } catch (err) {
      console.warn('Clipboard API failed, using fallback:', err);
      const tempTextArea = document.createElement('textarea');
      tempTextArea.value = emailContainer.outerHTML;
      document.body.appendChild(tempTextArea);
      tempTextArea.select();
      document.execCommand('copy');
      document.body.removeChild(tempTextArea);
    }

    return true;
  } catch (error) {
    console.error('Error generating signature:', error);
    throw new Error('Failed to copy email signature');
  }
};

// QR code initialization with improved logo handling
export const initializeQRCode = async ({ qrContainerRef, qrRef, cardUrl, emailSignatureState, cardId }) => {
  console.log("Card URL from initialize QR code: ", cardUrl);
  try {
    if (qrContainerRef.current && emailSignatureState[cardId]?.emailSignatureIncludeQRCode) {
      // Clear existing content
      qrContainerRef.current.innerHTML = '';

      // Calculate optimal size for logo within QR code
      const qrSize = 140;
      const logoSize = Math.floor(qrSize * 0.3); // 30% of QR code size

      // Initialize QR code with improved settings
      qrRef.current = new QRCodeStyling({
        width: qrSize,
        height: qrSize,
        type: "svg",
        data: cardUrl,
        image: emailSignatureState[cardId]?.emailSignatureQRCodeLogoUrl,
        dotsOptions: {
          color: "#000000",
          type: "square",
          gradient: null
        },
        cornersSquareOptions: {
          type: "square",
          color: "#000000"
        },
        cornersDotOptions: {
          type: "square",
          color: "#000000"
        },
        backgroundOptions: {
          color: "#ffffff",
        },
        imageOptions: {
          crossOrigin: "anonymous",
          margin: 3,
          imageSize: 0.7, // 30% of QR code size
          hideBackgroundDots: true,
          logoRadius: 0 // Remove logo border radius
        },
        qrOptions: {
          errorCorrectionLevel: "H", // Highest error correction for better logo display
          quality: 1,
          margin: 1
        }
      });

      // Append QR code to container
      await qrRef.current.append(qrContainerRef.current);

      // Post-process the SVG to improve logo quality
      const svg = qrContainerRef.current.querySelector('svg');
      if (svg) {
        // Ensure SVG uses crisp edges
        svg.style.shapeRendering = 'crispEdges';

        // Find and improve logo image if it exists
        const logoImage = svg.querySelector('image');
        if (logoImage) {
          logoImage.setAttribute('width', `${logoSize}`);
          logoImage.setAttribute('height', `${logoSize}`);
          // Center the logo
          const x = (qrSize - logoSize) / 2;
          const y = (qrSize - logoSize) / 2;
          logoImage.setAttribute('x', `${x}`);
          logoImage.setAttribute('y', `${y}`);
          // Improve image rendering
          logoImage.style.imageRendering = '-webkit-optimize-contrast';
          logoImage.style.imageRendering = 'crisp-edges';
        }
      }

      return true;
    }
    return false;
  } catch (err) {
    console.error('QR Code initialization error:', err);
    throw new Error('Failed to generate QR code');
  }
};

// Add new function to combine images
const combineProfileAndLogo = async (profileImageUrl, companyLogoUrl) => {
  const loadImage = async (url) => {
    console.log(`Loading image from: ${url}`);
    const img = new Image();
    img.crossOrigin = 'anonymous';
    return new Promise((resolve, reject) => {
      img.onload = () => {
        console.log(`Loaded image dimensions:`, {
          url,
          naturalWidth: img.naturalWidth,
          naturalHeight: img.naturalHeight
        });
        resolve(img);
      };
      img.onerror = reject;
      img.src = url;
    });
  };

  try {
    // Load images
    const [profileImage, logoImage] = await Promise.all([
      loadImage(profileImageUrl),
      companyLogoUrl ? loadImage(companyLogoUrl) : Promise.resolve(null)
    ]);

    // Work with original high resolution
    const size = 800; // Much larger canvas size
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d', { alpha: true });

    // Clear canvas
    ctx.clearRect(0, 0, size, size);

    // Draw profile image in circle
    ctx.save();
    ctx.beginPath();
    ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
    ctx.closePath();
    ctx.clip();

    // Calculate scale to fill circle while maintaining aspect ratio
    const scale = Math.max(size / profileImage.width, size / profileImage.height);
    const width = profileImage.width * scale;
    const height = profileImage.height * scale;
    const x = (size - width) / 2;
    const y = (size - height) / 2;

    // Draw profile with high quality settings
    ctx.drawImage(profileImage, x, y, width, height);
    ctx.restore();

    // Add white border
    ctx.beginPath();
    ctx.arc(size / 2, size / 2, size / 2 - 2, 0, Math.PI * 2);
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 4;
    ctx.stroke();

    if (logoImage) {
      // Logo size ~35% of main image
      const logoSize = Math.round(size * 0.35);
      const logoX = size - logoSize - 16;
      const logoY = size - logoSize - 16;

      // White circle background for logo
      ctx.save();
      ctx.beginPath();
      ctx.arc(logoX + logoSize / 2, logoY + logoSize / 2, logoSize / 2 + 8, 0, Math.PI * 2);
      ctx.fillStyle = '#ffffff';
      ctx.shadowColor = 'rgba(0, 0, 0, 0.2)';
      ctx.shadowBlur = 20;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 4;
      ctx.fill();
      ctx.restore();

      // Draw logo
      ctx.save();
      ctx.beginPath();
      ctx.arc(logoX + logoSize / 2, logoY + logoSize / 2, logoSize / 2, 0, Math.PI * 2);
      ctx.clip();

      // Calculate logo scaling
      const logoAspect = logoImage.width / logoImage.height;
      let logoDrawWidth = logoSize;
      let logoDrawHeight = logoSize;
      let logoDrawX = logoX;
      let logoDrawY = logoY;

      if (logoAspect > 1) {
        // Wide logo
        logoDrawHeight = logoSize / logoAspect;
        logoDrawY += (logoSize - logoDrawHeight) / 2;
      } else if (logoAspect < 1) {
        // Tall logo
        logoDrawWidth = logoSize * logoAspect;
        logoDrawX += (logoSize - logoDrawWidth) / 2;
      }

      ctx.drawImage(logoImage, logoDrawX, logoDrawY, logoDrawWidth, logoDrawHeight);
      ctx.restore();

      // Add subtle border around logo
      ctx.beginPath();
      ctx.arc(logoX + logoSize / 2, logoY + logoSize / 2, logoSize / 2, 0, Math.PI * 2);
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.8)';
      ctx.lineWidth = 2;
      ctx.stroke();
    }

    // Return high resolution image
    const dataUrl = canvas.toDataURL('image/png', 1.0);
    console.log('Generated high-res image');
    return dataUrl;
  } catch (error) {
    console.error('Error combining images:', error);
    return profileImageUrl; // Fallback to original
  }
};

export const copyEmailSignatureHTML = async ({ state, contentRef, cardUrl }) => {
  try {
    console.log('copyEmailSignatureHTML called with:', { state, cardUrl });

    // Create empty images object with the correct structure
    const images = {
      profileImage: state.emailSignatureProfilePictureUrl || '',
      companyLogo: state.emailSignatureCompanyLogoUrl || '',
      qrCode: state.emailSignatureQRCodeUrl || '',
      bannerImage: state.emailSignatureBannerImageUrl || ''
    };

    console.log('Images object:', images);

    // Generate the HTML code
    const htmlCode = generateSignatureHTML(state, images, cardUrl);

    console.log('Generated HTML code:', htmlCode);

    try {
      // Focus the document first
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
      window.focus();

      // Copy the HTML code to the clipboard
      await navigator.clipboard.writeText(htmlCode);
      console.log('HTML code copied to clipboard successfully');
    } catch (err) {
      console.warn('Clipboard API failed, using fallback:', err);
      const tempTextArea = document.createElement('textarea');
      tempTextArea.value = htmlCode;
      document.body.appendChild(tempTextArea);
      tempTextArea.select();
      document.execCommand('copy');
      document.body.removeChild(tempTextArea);
    }

    return true;
  } catch (error) {
    console.error('Error generating HTML:', error);
    throw new Error('Failed to copy HTML code');
  }
};

// COMPLETELY REDESIGNED Gmail-friendly HTML Generator Function 
const generateSignatureHTML = (state, images, cardUrl) => {
  // Check if profile image exists
  const hasProfileImage = !!images.profileImage;
  const hasCompanyLogo = !!images.companyLogo;

  // Gmail-optimized signature HTML with strict table-based layout
  return `
    <table cellpadding="0" cellspacing="0" border="0" style="width: 500px; font-family: Arial, sans-serif; border-collapse: collapse;">
      <tr>
        <!-- LEFT COLUMN: Profile Image + Text -->
        <td valign="top" style="padding: ${hasProfileImage ? '20px 0 0 20px' : '40px 0 0 20px'}; width: 220px;">
          <table cellpadding="0" cellspacing="0" border="0" style="width: 100%;">
            ${hasProfileImage ? `
            <!-- Profile Image Row - Only shown when profile image exists -->
            <tr>
              <td style="padding-bottom: 15px;">
                <img src="${images.profileImage}" alt="Profile" width="100" height="100" style="border-radius: 50%; display: block;">
              </td>
            </tr>
            ` : ''}
            
            <!-- Name Row -->
            <tr>
              <td style="font-size: 16px; font-weight: bold; padding-bottom: 4px; color: #212121;">
                ${state.emailSignatureName || ''}
              </td>
            </tr>
            
            ${state.emailSignatureJobTitle ? `
            <!-- Job Title Row -->
            <tr>
              <td style="font-size: 12px; color: #4f4f4f; padding-bottom: 2px;">
                ${state.emailSignatureJobTitle}
              </td>
            </tr>
            ` : ''}
            
            ${state.emailSignatureCompany ? `
            <!-- Company Row -->
            <tr>
              <td style="font-size: 12px; color: #4f4f4f; padding-bottom: 6px;">
                ${state.emailSignatureCompany}
              </td>
            </tr>
            ` : ''}
            
            ${state.emailSignatureLocation ? `
            <!-- Location Row -->
            <tr>
              <td style="font-size: 12px; color: #828282; padding-top: 6px;">
                ${state.emailSignatureLocation}
              </td>
            </tr>
            ` : ''}
            
            ${state.emailSignaturePhoneNumber ? `
            <!-- Phone Number Row -->
            <tr>
              <td style="font-size: 12px; color: #828282;">
                ${state.emailSignaturePhoneNumber}
              </td>
            </tr>
            ` : ''}
            
            ${/* Company Logo Row - Only shown when no profile image but has company logo - NOW AFTER ALL TEXT */ ''}
            ${!hasProfileImage && hasCompanyLogo ? `
            <tr>
              <td style="padding: 16px 0 8px 0;">
                <img src="${images.companyLogo}" alt="Company Logo" width="60" height="60" style="border-radius: 50%; display: block;">
              </td>
            </tr>
            ` : ''}
          </table>
        </td>
        
        <!-- SPACER COLUMN -->
        <td style="width: 40px;"></td>
        
        <!-- RIGHT COLUMN: QR Code -->
        ${state.emailSignatureIncludeQRCode ? `
        <td valign="top" style="padding: 20px 20px 0 0; width: 200px;">
          <table cellpadding="0" cellspacing="0" border="0" style="width: 100%;">
            <tr>
              <td align="center" style="padding-bottom: 5px;">
                <span style="font-size: 12px; color: #828282; font-weight: 500;">Connect with me</span>
              </td>
            </tr>
            ${images.qrCode ? `
            <tr>
              <td align="center" style="padding-bottom: 5px;">
                <img src="${images.qrCode}" alt="QR Code" width="140" style="display: block;">
              </td>
            </tr>
            ` : ''}
            <tr>
              <td align="center">
                <a href="${cardUrl}" target="_blank" style="font-size: 12px; color: #2aaff7; text-decoration: none;">
                  My Digital Business Card
                </a>
              </td>
            </tr>
          </table>
        </td>
        ` : ''}
      </tr>
      
      ${state.emailSignatureIncludeBannerImage && images.bannerImage ? `
      <!-- Banner Image Row -->
      <tr>
        <td colspan="3" style="padding: 15px 20px 0 20px;">
          <img src="${images.bannerImage}" alt="Banner" width="100%" style="display: block; max-width: 500px;">
        </td>
      </tr>
      ` : ''}
      
      ${state.emailSignatureDisclaimer ? `
      <!-- Disclaimer Row -->
      <tr>
        <td colspan="3" style="padding: 15px 20px 20px 20px; font-size: 11px; color: #bdbdbd; font-style: italic;">
          ${state.emailSignatureDisclaimer}
        </td>
      </tr>
      ` : ''}
    </table>
  `;
};

export { combineProfileAndLogo };